/**
 * Small DOM controls shared by the arcade mini-games.
 *
 * These widgets are presentation-only. They make score pills and command
 * buttons consistent across games, while every game keeps its structural rules
 * in its own pure model.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";

export interface ArcadeHotkeyBinding {
  readonly key?: string;
  readonly code?: string;
  readonly enabled?: boolean;
  readonly onTrigger: () => void;
}

export interface ArcadeHotkeyEventLike {
  readonly altKey?: boolean;
  readonly ctrlKey?: boolean;
  readonly metaKey?: boolean;
  readonly repeat?: boolean;
  readonly shiftKey?: boolean;
  readonly target: EventTarget | null;
}

export function useArcadeHotkeys(
  bindings: readonly ArcadeHotkeyBinding[],
): void {
  const bindingsRef = React.useRef(bindings);

  React.useEffect(() => {
    bindingsRef.current = bindings;
  }, [bindings]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (shouldIgnoreArcadeHotkeyEvent(event)) return;
      for (const binding of bindingsRef.current) {
        if (binding.enabled === false) continue;
        const keyMatches =
          event.key.toLowerCase() === binding.key?.toLowerCase();
        const codeMatches = event.code === binding.code;
        if (!keyMatches && !codeMatches) continue;
        event.preventDefault();
        binding.onTrigger();
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return (): void => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
}

export interface ArcadeActionHotkeysOptions {
  readonly enabled?: boolean;
  readonly extraBindings?: readonly ArcadeHotkeyBinding[];
  readonly includeEnter?: boolean;
  readonly onPrimary?: () => void;
  readonly onReset?: () => void;
  readonly primaryEnabled?: boolean;
}

export function useArcadeActionHotkeys({
  enabled = true,
  extraBindings = [],
  includeEnter = true,
  onPrimary,
  onReset,
  primaryEnabled = enabled,
}: ArcadeActionHotkeysOptions): void {
  useArcadeHotkeys([
    ...extraBindings,
    ...(onPrimary === undefined
      ? []
      : [
          { code: "Space", enabled: primaryEnabled, onTrigger: onPrimary },
          ...(includeEnter
            ? [{ key: "Enter", enabled: primaryEnabled, onTrigger: onPrimary }]
            : []),
        ]),
    ...(onReset === undefined ? [] : [{ key: "r", onTrigger: onReset }]),
  ]);
}

export interface ArcadeAutoClockOptions {
  readonly active: boolean;
  readonly onPause: () => void;
  readonly onStart: () => void;
}

export function useArcadeAutoClock({
  active,
  onPause,
  onStart,
}: ArcadeAutoClockOptions): void {
  React.useEffect(() => {
    onStart();
  }, [onStart]);

  React.useEffect(() => {
    if (!active) onPause();
  }, [active, onPause]);
}

export function shouldIgnoreArcadeHotkeyEvent(
  event: ArcadeHotkeyEventLike,
): boolean {
  return (
    event.altKey === true ||
    event.ctrlKey === true ||
    event.metaKey === true ||
    event.repeat === true ||
    event.shiftKey === true ||
    isArcadeHotkeyInteractiveTarget(event.target)
  );
}

function isArcadeHotkeyInteractiveTarget(target: EventTarget | null): boolean {
  if (target === null || typeof target !== "object") return false;
  const element = target as {
    readonly closest?: (selector: string) => Element | null;
    readonly isContentEditable?: boolean;
    readonly tagName?: string;
  };
  const interactiveAncestor = element.closest?.(
    [
      "input",
      "textarea",
      "select",
      "button",
      "a[href]",
      "summary",
      "[role='button']",
      "[role='link']",
      "[role='menuitem']",
      "[contenteditable='']",
      "[contenteditable='true']",
    ].join(","),
  );
  if (interactiveAncestor) return true;
  if (element.isContentEditable === true) return true;
  const tagName = element.tagName?.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    tagName === "button" ||
    tagName === "a" ||
    tagName === "summary"
  );
}

export interface ArcadeStatPillProps {
  readonly label: string;
  readonly value: string | number;
  readonly color: string;
  readonly title?: string;
}

export function ArcadeStatPill({
  label,
  value,
  color,
  title,
}: ArcadeStatPillProps): React.JSX.Element {
  return (
    <span
      title={title}
      style={{
        alignItems: "center",
        background: `${color}18`,
        border: `1px solid ${color}66`,
        borderRadius: 999,
        color,
        display: "inline-flex",
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: 11,
        fontVariantNumeric: "tabular-nums",
        fontWeight: 800,
        gap: 6,
        lineHeight: 1,
        padding: "5px 10px",
        textTransform: "uppercase",
      }}
    >
      <span style={{ opacity: 0.82 }}>{label}</span>
      <strong style={{ color: "inherit" }}>{value}</strong>
    </span>
  );
}

export interface ArcadeActionButtonProps {
  readonly children: React.ReactNode;
  readonly color: string;
  readonly onClick?: () => void;
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly icon?: React.ReactNode;
  readonly onPointerCancel?: () => void;
  readonly onPointerDown?: () => void;
  readonly onPointerLeave?: () => void;
  readonly onPointerUp?: () => void;
  readonly style?: React.CSSProperties;
}

export function ArcadeActionButton({
  children,
  color,
  onClick,
  ariaLabel,
  className,
  disabled = false,
  icon,
  onPointerCancel,
  onPointerDown,
  onPointerLeave,
  onPointerUp,
  style,
}: ArcadeActionButtonProps): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      className={className}
      onClick={onClick}
      onPointerCancel={onPointerCancel}
      onPointerDown={onPointerDown}
      onPointerLeave={onPointerLeave}
      onPointerUp={onPointerUp}
      style={{
        alignItems: "center",
        background: disabled
          ? "rgba(15,23,42,0.48)"
          : `linear-gradient(180deg, ${color}2E, rgba(2,6,23,0.62))`,
        border: `1px solid ${disabled ? "rgba(148,163,184,0.28)" : `${color}AA`}`,
        borderRadius: 10,
        color: disabled ? "rgba(203,213,225,0.48)" : "#f8fafc",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        fontSize: 12,
        fontWeight: 850,
        gap: 8,
        justifyContent: "center",
        minHeight: 40,
        padding: "9px 12px",
        textAlign: "left",
        transition: "transform 160ms ease, background 160ms ease",
        ...style,
      }}
    >
      {icon ? (
        <span aria-hidden style={{ color, fontWeight: 900 }}>
          {icon}
        </span>
      ) : null}
      <span>{children}</span>
    </button>
  );
}

export interface ArcadeControlButtonProps {
  readonly label: string;
  readonly accent: string;
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly onClick?: () => void;
  readonly onPointerCancel?: () => void;
  readonly onPointerDown?: () => void;
  readonly onPointerLeave?: () => void;
  readonly onPointerUp?: () => void;
  readonly style?: React.CSSProperties;
}

export function ArcadeControlButton({
  label,
  accent,
  children,
  className,
  disabled,
  onClick,
  onPointerCancel,
  onPointerDown,
  onPointerLeave,
  onPointerUp,
  style,
}: ArcadeControlButtonProps): React.JSX.Element {
  return (
    <ArcadeActionButton
      ariaLabel={label}
      className={className}
      color={accent}
      disabled={disabled}
      onClick={onClick}
      onPointerCancel={onPointerCancel}
      onPointerDown={onPointerDown}
      onPointerLeave={onPointerLeave}
      onPointerUp={onPointerUp}
      style={{ minWidth: 48, ...style }}
    >
      {children}
    </ArcadeActionButton>
  );
}

export interface ArcadeRunControlsProps {
  readonly clockRunning: boolean;
  readonly disabledPrimary?: boolean;
  readonly onPrimary: () => void;
  readonly onReset: () => void;
  readonly onToggleClock: () => void;
  readonly primaryAriaLabel: string;
  readonly primaryColor: string;
  readonly primaryIcon?: string;
  readonly primaryLabel: string;
  readonly resetAriaLabel: string;
  readonly resetColor?: string;
  readonly toggleColor?: string;
}

export function ArcadeRunControls({
  clockRunning,
  disabledPrimary = false,
  onPrimary,
  onReset,
  onToggleClock,
  primaryAriaLabel,
  primaryColor,
  primaryIcon = ">",
  primaryLabel,
  resetAriaLabel,
  resetColor = "#ef4444",
  toggleColor = "#22c55e",
}: ArcadeRunControlsProps): React.JSX.Element {
  return (
    <div className="grid gap-2">
      <ArcadeActionButton
        color={disabledPrimary ? "#64748b" : primaryColor}
        disabled={disabledPrimary}
        onClick={onPrimary}
        ariaLabel={primaryAriaLabel}
        icon={primaryIcon}
      >
        {primaryLabel}
      </ArcadeActionButton>
      <div className="grid grid-cols-2 gap-2">
        <ArcadeActionButton
          color={toggleColor}
          onClick={onToggleClock}
          ariaLabel={clockRunning ? "Pause timer" : "Run timer"}
          icon={clockRunning ? "||" : ">"}
        >
          {clockRunning ? "Pause" : "Run"}
        </ArcadeActionButton>
        <ArcadeActionButton
          color={resetColor}
          onClick={onReset}
          ariaLabel={resetAriaLabel}
          icon="R"
        >
          Reset
        </ArcadeActionButton>
      </div>
    </div>
  );
}

export interface ArcadeMissionPromptProps {
  readonly label: string;
  readonly title: string;
  readonly detail: string;
  readonly color: string;
  readonly hotkey?: string;
  readonly status?: string;
}

export function ArcadeMissionPrompt({
  label,
  title,
  detail,
  color,
  hotkey,
  status,
}: ArcadeMissionPromptProps): React.JSX.Element {
  return (
    <div
      style={{
        alignItems: "center",
        background: `linear-gradient(135deg, ${color}1F, rgba(2,6,23,0.54))`,
        border: `1px solid ${color}77`,
        borderRadius: 16,
        boxShadow: `0 0 34px ${color}18`,
        display: "grid",
        gap: 10,
        gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))",
        padding: "12px 14px",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            color,
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: 11,
            fontWeight: 850,
            letterSpacing: 0,
            textTransform: "uppercase",
          }}
        >
          {label}
        </div>
        <div
          style={{
            color: "#f8fafc",
            fontSize: 15,
            fontWeight: 900,
            lineHeight: 1.2,
            marginTop: 4,
          }}
        >
          {title}
        </div>
        <div
          style={{
            color: "rgba(203,213,225,0.78)",
            fontSize: 12,
            lineHeight: 1.45,
            marginTop: 5,
          }}
        >
          {detail}
        </div>
      </div>
      <div
        style={{
          alignItems: "flex-end",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          minWidth: 74,
        }}
      >
        {hotkey ? (
          <kbd
            style={{
              background: "rgba(2,6,23,0.72)",
              border: `1px solid ${color}77`,
              borderRadius: 10,
              color,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: 11,
              fontWeight: 900,
              padding: "6px 9px",
            }}
          >
            {hotkey}
          </kbd>
        ) : null}
        {status ? (
          <span
            style={{
              color: "rgba(226,232,240,0.78)",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: 10,
              fontWeight: 800,
              textTransform: "uppercase",
            }}
          >
            {status}
          </span>
        ) : null}
      </div>
    </div>
  );
}
